import React, { memo, useMemo, useRef, useEffect, useCallback } from "react";
import { Container, useApp } from "@pixi/react";
import SpineTextureExtractor from "../../utils/SpineTextureExtractor";
import TickerTimeout from "../../utils/TickerTimeout";
import HoldAndWinCell from "./HoldAndWinCell";
import { resetHoldAndWinReelStops } from "./HoldAndWinCellController";
import { jackpotCoinSkin } from "./jackpotCoin";
import {
  BLANK_SYMBOL_ID,
  COIN_SYMBOL_ID,
  COIN_JACKPOT_SYMBOL_ID,
  CHEST_SYMBOL_ID,
  GOLDEN_CHEST_SYMBOL_ID,
  SCARAB_SYMBOL_ID,
  JACKPOT_NAME_TO_SKIN,
  symbolData,
} from "../../SymbolData";

/**
 * HoldAndWinBoard — the Cleopatra's Fire Hold & Win grid, 5 x 8.
 *
 * Forty HoldAndWinCells, each spinning on its own (HoldAndWinCellController).
 * Centred on the room's reel slot, whose bone sits on the centre of the 5 x 8
 * cell grid drawn into the frame. `cellWidth` / `cellHeight` are one cell in
 * container pixels; the art is fitted to the cell height, so the 170 x 139
 * cells keep square symbols.
 *
 * Cells are addressed [column][row], row 0 = the TOP screen row.
 *
 * `overlayRef` — the HoldAndWinOverlay in the room's front slot. A cell that
 * lands on anything but a blank is handed to it: the overlay plays the
 * symbol's spine (in -> idle) and the cell's static picture is hidden.
 *
 * `devSpinLoop` — TEMPORARY, for looking at the room: spins every cell,
 * stops them one by one on random symbols, and repeats. No state, no server.
 *
 * `kinds` / `values` — the server's grid (holdAndWin slice, SCREEN rows,
 * [column][row]). Drawn as it stands whenever the dev loop is off: every
 * non-empty cell plays its spine on the overlay, the rest stay empty. NOT
 * while a respin is in flight — the respin lands its own cells.
 *
 * `controlRef` — receives the respin handle HoldAndWinControlContext drives:
 *   spinEmpty(kinds)             spin every empty cell, returns their [c, r]
 *   land(cells, kinds, values)   stop them in order onto the server's grid;
 *                                resolves when the last one has landed
 *   quickStop()                  land everything still turning, now
 *   skipAnticipationCell()       a press during the last cells' anticipation:
 *                                the cell on its turn stops now (true = taken)
 *   freeze()                     stop dead where they are (a refused respin)
 *   restore()                    after the error popup: settle the frozen
 *                                cells and draw the grid again
 */
export const HOLD_AND_WIN_COLUMNS = 5;
export const HOLD_AND_WIN_ROWS = 8;

// What the dev loop lands on: mostly blanks, some coins, a chest, a scarab.
const DEV_LANDING_POOL = [
  BLANK_SYMBOL_ID,
  BLANK_SYMBOL_ID,
  BLANK_SYMBOL_ID,
  BLANK_SYMBOL_ID,
  9, // coin
  9,
  12, // chest
  14, // scarab
];
const DEV_COIN_VALUES = [20, 40, 60, 100, 200];
const DEV_SPIN_MS = 600; // spin before the first cell stops
const DEV_STOP_GAP_MS = 40; // between one cell's stop and the next
const DEV_REST_MS = 1500; // landed board on screen before the next spin

// bonus_data.cell_kinds -> the grid sprite (contract §6). `pending_chest` is a
// chest waiting for its row; it draws as a chest.
const KIND_TO_SYMBOL = {
  coin: COIN_SYMBOL_ID,
  chest: CHEST_SYMBOL_ID,
  pending_chest: CHEST_SYMBOL_ID,
  golden_chest: GOLDEN_CHEST_SYMBOL_ID,
  scarab: SCARAB_SYMBOL_ID,
};

/**
 * A cell's symbol: the kind's sprite — except a coin whose points the math
 * makes a jackpot, which is `cj` with that jackpot's skin (jackpotCoin.js).
 */
const resolveCell = (kind, points, jackpots) => {
  const id = KIND_TO_SYMBOL[kind];
  if (id === COIN_SYMBOL_ID) {
    const skin = jackpotCoinSkin(points, jackpots);
    if (skin) return { id: COIN_JACKPOT_SYMBOL_ID, skin };
  }
  return { id, skin: null };
};

/**
 * Draw the server's grid as it stands: every non-empty cell plays its spine
 * on the overlay (its static picture hidden), the rest show empty.
 */
const paintGrid = (cellRefs, overlay, { kinds, values, points, jackpots }) => {
  for (let c = 0; c < HOLD_AND_WIN_COLUMNS; c++) {
    for (let r = 0; r < HOLD_AND_WIN_ROWS; r++) {
      const cell = cellRefs.current[c][r]?.current;
      const { id, skin } = resolveCell(kinds?.[c]?.[r], points?.[c]?.[r], jackpots);
      const value = values?.[c]?.[r] || null;
      if (id !== undefined && overlay?.play(c, r, id, value, skin)) {
        cell?.hide();
      } else {
        overlay?.clear(c, r);
        cell?.show(BLANK_SYMBOL_ID);
      }
    }
  }
};

const HoldAndWinBoard = memo(({
  cellWidth,
  cellHeight,
  overlayRef,
  devSpinLoop = false,
  kinds = null,
  values = null,
  points = null,
  jackpots = null,
  controlRef = null,
}) => {
  const app = useApp();
  const cellRefs = useRef(
    Array.from({ length: HOLD_AND_WIN_COLUMNS }, () =>
      Array.from({ length: HOLD_AND_WIN_ROWS }, () => React.createRef()),
    ),
  );

  // ─── Respin state ───
  // busy: a respin owns the board (spinning or landing) — the grid effect
  // below keeps its hands off. pending: cells still to land this respin.
  // stops: the scheduled per-cell stops, cleared by a quick stop.
  // const respin = useRef({ busy: false, pending: new Set(), resolve: null, stops: [], queue: [] });
  // chain: the last cells' anticipation running now (land, cellAnticipation.js).
  const respin = useRef({
    busy: false,
    pending: new Set(),
    resolve: null,
    stops: [],
    queue: [],
    chain: null,
    // A chain due MID-respin: { trigger, tail, start } — it starts as the
    // `trigger` cell lands (land, startAt > 0).
    chainStart: null,
  });
  // The jackpot skin each landing cell is waiting to show ("c_r" -> skin).
  const landingSkins = useRef(new Map());
  // The server's grid as last rendered — restore() repaints from it.
  const gridRef = useRef(null);
  gridRef.current = { kinds, values, points, jackpots };

  // Landed: hand the cell to its overlay spine. Stable, so cells never
  // re-render for it.
  const handleLanded = useCallback(
    (col, row, symbolId, value) => {
      const skin = landingSkins.current.get(`${col}_${row}`) ?? null;
      landingSkins.current.delete(`${col}_${row}`);
      const played = overlayRef?.current?.play(col, row, symbolId, value, skin);
      if (played) cellRefs.current[col][row]?.current?.hide();
      else overlayRef?.current?.clear(col, row);

      const r = respin.current;
      // The owner hears each cell AS it lands (the respin counter resets on
      // the first qualifying landing, not after the whole respin).
      r.onLanded?.(col, row, symbolId);
      // ── The last cells' anticipation, due as THIS cell lands ── (the 38th of
      // 40): the cells still spinning go into it now, in this respin.
      if (r.chainStart?.trigger === `${col}_${row}`) {
        const { start } = r.chainStart;
        r.chainStart = null;
        start();
      }
      if (r.pending.delete(`${col}_${row}`) && r.pending.size === 0) {
        r.busy = false;
        const resolve = r.resolve;
        r.resolve = null;
        resolve?.();
      }
    },
    [overlayRef],
  );

  // ─── The respin handle ───
  useEffect(() => {
    if (!controlRef) return undefined;
    const cellAt = (c, r) => cellRefs.current[c]?.[r]?.current;
    const clearStops = () => {
      respin.current.stops.forEach((t) => t?.clear?.());
      respin.current.stops = [];
    };

    // ── The last cells' anticipation (Vladimir, 2026-10-07) ──
    // The base game's chain (anticipationRunner), cell by cell — see
    // cellAnticipation.js. It STARTS WITH THE SPIN (Pot Collection: "works
    // instantly during spin"): how many cells are left is known before the
    // server answers. The answer (land) only sets what each cell lands on and
    // where the chain ends; a turn that is over before it arrives keeps
    // looping until it does. The chain keeps its OWN timers — land() clears
    // the respin's stops when the answer comes, and must not cut the chain.
    const cellKey = ([c, row]) => `${c}_${row}`;
    const cancelChain = () => {
      respin.current.chainStart = null;
      const ch = respin.current.chain;
      if (!ch) return null;
      respin.current.chain = null;
      ch.done = true;
      ch.timers.forEach((t) => t?.clear?.());
      ch.timers = [];
      overlayRef?.current?.hideCellAnticipation?.();
      ch.hooks?.onCancel?.();
      return ch;
    };

    // `cells`: the spinning cells, stop order (all of them are candidates
    // until the answer says where the hope ends).
    const beginChain = (cells, hooks) => {
      cancelChain();
      const r = respin.current;
      const overlay = () => overlayRef?.current;
      const t = overlay()?.cellAnticipationTiming?.() ?? { inMs: 0, loopMs: 1000, outMs: 0 };
      const loops = hooks.loops ?? 1;
      const ch = {
        cells: cells.slice(),
        rest: [],
        hooks,
        step: -1,
        ended: -1,
        turn: null,
        done: false,
        // ready: the answer is here (targets set). due: the turn on screen
        // is over (or was skipped) and waits only for it.
        ready: false,
        due: false,
        timers: [],
        stopRest: null,
      };
      r.chain = ch;
      const live = () => r.chain === ch && !ch.done;
      const later = (fn, ms) => {
        const timer = new TickerTimeout(fn, ms);
        ch.timers.push(timer);
        return timer;
      };
      // Chain cell `i` stops — its out plays FROM the stop, as a base chain
      // reel's does — then the next cell's turn, or the rest.
      const endTurn = (i) => {
        if (!live() || ch.step !== i || ch.ended >= i) return;
        if (!ch.ready) {
          ch.due = true; // keeps looping until the answer lands it
          return;
        }
        ch.ended = i;
        ch.turn?.clear?.();
        ch.turn = null;
        const [c, row] = ch.cells[i];
        cellAt(c, row)?.stop(false);
        overlay()?.stopCellAnticipation?.();
        const last = i + 1 >= ch.cells.length;
        // The room frame fades WITH the last cell's out, not after it.
        if (last) hooks.onEnd?.();
        later(() => {
          if (!live()) return;
          if (!last) {
            startTurn(i + 1);
            return;
          }
          ch.done = true;
          r.chain = null;
          ch.stopRest?.(ch.rest);
        }, t.outMs);
      };
      // Chain cell `i`: in -> loop on it, still spinning; its turn is over
      // after in + LOOPS x loop.
      const startTurn = (i) => {
        if (!live()) return;
        ch.step = i;
        ch.due = false;
        const [c, row] = ch.cells[i];
        if (i === 0) hooks.onStart?.();
        overlay()?.playCellAnticipation?.(c, row);
        ch.turn = later(() => {
          ch.turn = null;
          endTurn(i);
        }, t.inMs + t.loopMs * loops);
      };
      // A press: the cell on its turn stops now — or the moment the answer
      // is here (its out still plays in full). During an out, nothing; the
      // next press takes the next cell.
      ch.skip = () => {
        if (!live() || ch.step < 0 || ch.ended >= ch.step) return;
        if (ch.ready) endTurn(ch.step);
        else ch.due = true;
      };
      // The answer: the chain ends WITH its first empty landing (`chainCells`,
      // a prefix of ours); the cells behind it stop with `rest` afterwards.
      ch.land = (chainCells, rest, stopRest) => {
        if (!live()) return;
        const keep = new Set(chainCells.map(cellKey));
        ch.cells = ch.cells.filter((cell, idx) => idx <= ch.step || keep.has(cellKey(cell)));
        const inChain = new Set(ch.cells.map(cellKey));
        ch.rest = rest.filter((cell) => !inChain.has(cellKey(cell)));
        ch.stopRest = stopRest;
        ch.ready = true;
        if (ch.due) endTurn(ch.step);
      };
      startTurn(0);
    };

    const api = {
      spinEmpty(currentKinds) {
        cancelChain();
        // Every column's stop is owed again (once per reel per respin).
        resetHoldAndWinReelStops();
        const cells = [];
        // Column by column, top to bottom — the order the cells will stop in.
        for (let c = 0; c < HOLD_AND_WIN_COLUMNS; c++) {
          for (let r = 0; r < HOLD_AND_WIN_ROWS; r++) {
            if (KIND_TO_SYMBOL[currentKinds?.[c]?.[r]] !== undefined) continue;
            const cell = cellAt(c, r);
            if (!cell) continue;
            overlayRef?.current?.clear(c, r);
            cell.spin();
            cells.push([c, r]);
          }
        }
        respin.current.busy = cells.length > 0;
        return cells;
      },

      /** The last cells' anticipation starts now, with the spin. */
      beginAnticipation(cells, hooks) {
        if (!cells?.length) return false;
        beginChain(cells, hooks || {});
        return true;
      },

      land(
        cells,
        nextKinds,
        nextValues,
        gapMs = 0,
        nextPoints = null,
        nextJackpots = null,
        onLanded = null,
        // The last cells' anticipation (cellAnticipationPlan): { startAt,
        // cells, hooks } — startAt 0: the chain begun with the spin, cut here;
        // startAt > 0: the first startAt cells stop normally and the chain
        // starts as the last of them lands. Or null.
        chain = null,
      ) {
        const r = respin.current;
        r.onLanded = onLanded;
        clearStops();
        if (!cells.length) {
          cancelChain();
          r.busy = false;
          return Promise.resolve();
        }
        r.pending = new Set(cells.map(([c, row]) => `${c}_${row}`));
        r.queue = cells.slice();
        const done = new Promise((resolve) => {
          r.resolve = resolve;
        });
        // Every target first, then the stops one by one.
        cells.forEach(([c, row]) => {
          const cell = resolveCell(
            nextKinds?.[c]?.[row],
            nextPoints?.[c]?.[row],
            nextJackpots,
          );
          const id = cell.id ?? BLANK_SYMBOL_ID;
          const value = id === BLANK_SYMBOL_ID ? null : nextValues?.[c]?.[row] || null;
          if (cell.skin) landingSkins.current.set(`${c}_${row}`, cell.skin);
          // cellAt(c, row)?.setLanding(id, value);
          // The skin too: the strip lands on the tier's picture, not the
          // bare coin the spine's word then popped in over.
          cellAt(c, row)?.setLanding(id, value, cell.skin);
        });
        // cells.forEach(([c, row], i) => {
        //   r.stops.push(
        //     new TickerTimeout(() => {
        //       r.queue = r.queue.filter(([qc, qr]) => qc !== c || qr !== row);
        //       cellAt(c, row)?.stop(false);
        //     }, i * gapMs),
        //   );
        // });
        const stopInOrder = (list) => {
          r.queue = list.slice();
          list.forEach(([c, row], i) => {
            r.stops.push(
              new TickerTimeout(() => {
                r.queue = r.queue.filter(([qc, qr]) => qc !== c || qr !== row);
                cellAt(c, row)?.stop(false);
              }, i * gapMs),
            );
          });
        };
        const running = r.chain;
        if (running && !running.done && chain?.cells?.length) {
          // The chain cells keep their turns; the cells after the chain
          // (behind a cell that landed empty) stop once it is over.
          r.queue = [];
          running.land(chain.cells, cells, stopInOrder);
          return done;
        }
        cancelChain();
        // ── The chain due MID-respin ── the cells before it stop as usual;
        // as the last of them lands (the 38th of 40), the ones still spinning
        // go straight into the anticipation (handleLanded).
        if (chain?.cells?.length && chain.startAt > 0) {
          const head = cells.slice(0, chain.startAt);
          const tail = cells.slice(chain.startAt);
          r.chainStart = {
            trigger: cellKey(head[head.length - 1]),
            tail,
            start: () => {
              beginChain(tail, chain.hooks || {});
              r.chain?.land(chain.cells, tail, stopInOrder);
            },
          };
          stopInOrder(head);
          return done;
        }
        stopInOrder(cells);
        return done;
      },

      skipAnticipationCell() {
        const ch = respin.current.chain;
        if (!ch || ch.done || ch.step < 0) return false;
        ch.skip();
        return true;
      },

      quickStop() {
        const r = respin.current;
        // const left = r.queue;
        // A chain still running ends at once: every cell it has not stopped
        // lands now with the rest (a press is normally its skip — this is the
        // hard stop). A chain not started yet (due mid-respin) never starts —
        // its cells land with the rest (Pot Collection: a press before the
        // anticipation's visuals stops every blank).
        const waiting = r.chainStart?.tail || [];
        const ch = cancelChain();
        const left = ch
          ? [...ch.cells.slice(ch.ended + 1), ...ch.rest]
          : [...r.queue, ...waiting];
        clearStops();
        r.queue = [];
        left.forEach(([c, row]) => cellAt(c, row)?.stop(true));
      },

      /** The live-spine layer over the board (chest collections). */
      overlay() {
        return overlayRef?.current ?? null;
      },

      freeze() {
        const r = respin.current;
        cancelChain();
        clearStops();
        r.queue = [];
        for (let c = 0; c < HOLD_AND_WIN_COLUMNS; c++) {
          for (let row = 0; row < HOLD_AND_WIN_ROWS; row++) cellAt(c, row)?.freeze();
        }
        r.pending.clear();
        r.busy = false;
        r.resolve = null;
      },

      // Error recovery, behind its curtain (ErrorRecoveryOverlay): the frozen
      // cells settle and the grid is drawn again — the base reels'
      // restoreReels. A frame later, so the refreshed grid has rendered.
      restore() {
        api.freeze();
        for (let c = 0; c < HOLD_AND_WIN_COLUMNS; c++) {
          for (let row = 0; row < HOLD_AND_WIN_ROWS; row++) cellAt(c, row)?.restore();
        }
        landingSkins.current.clear();
        overlayRef?.current?.hideCellAnticipation?.();
        respin.current.stops.push(
          new TickerTimeout(() => {
            if (respin.current.busy || !gridRef.current?.kinds) return;
            paintGrid(cellRefs, overlayRef?.current, gridRef.current);
          }, 50),
        );
      },
    };

    controlRef.current = api;
    return () => {
      cancelChain();
      clearStops();
      if (controlRef.current === api) controlRef.current = null;
    };
  }, [controlRef, overlayRef]);

  const spritesheet = useMemo(() => {
    if (app?.renderer) SpineTextureExtractor.initialize(app.renderer);
    return SpineTextureExtractor.getSymbols();
  }, [app?.renderer]);

  // The jackpot coin's tier pictures (MINI … GRAND), extracted once here, at
  // the board's build — not on the first jackpot coin to scroll by mid-spin.
  // Cached by SpineTextureExtractor; the base ReelSet may have done it already.
  useEffect(() => {
    if (!spritesheet?.textures) return;
    const spine = symbolData[COIN_JACKPOT_SYMBOL_ID]?.spine;
    if (!spine) return;
    for (const [name, skin] of Object.entries(JACKPOT_NAME_TO_SKIN)) {
      if (name !== "royal") SpineTextureExtractor.getSkinTexture(spine, skin);
    }
  }, [spritesheet]);

  // ─── Dev spin loop ───
  useEffect(() => {
    if (!devSpinLoop || !spritesheet?.textures) return undefined;

    let cancelled = false;
    const timers = [];
    const after = (ms, fn) => {
      timers.push(
        new TickerTimeout(() => {
          if (!cancelled) fn();
        }, ms),
      );
    };
    const eachCell = (fn) => {
      for (let c = 0; c < HOLD_AND_WIN_COLUMNS; c++) {
        for (let r = 0; r < HOLD_AND_WIN_ROWS; r++) {
          const cell = cellRefs.current[c][r]?.current;
          if (cell) fn(cell);
        }
      }
    };

    const round = () => {
      overlayRef?.current?.clearAll();
      eachCell((cell) => cell.spin());

      let order = 0;
      eachCell((cell) => {
        const id =
          DEV_LANDING_POOL[Math.floor(Math.random() * DEV_LANDING_POOL.length)];
        const value =
          id === 9
            ? DEV_COIN_VALUES[Math.floor(Math.random() * DEV_COIN_VALUES.length)]
            : null;
        cell.setLanding(id, value);
        after(DEV_SPIN_MS + order * DEV_STOP_GAP_MS, () => cell.stop(false));
        order += 1;
      });

      after(DEV_SPIN_MS + order * DEV_STOP_GAP_MS + DEV_REST_MS, round);
    };

    // Give the cells a frame to build their controllers.
    after(500, round);

    return () => {
      cancelled = true;
      timers.forEach((t) => t?.clear?.());
    };
  }, [devSpinLoop, spritesheet, overlayRef]);

  // ─── The server's grid ───
  // A frame after mount, so the cells have built their controllers.
  useEffect(() => {
    if (devSpinLoop || !kinds || !spritesheet?.textures) return undefined;
    if (respin.current.busy) return undefined;
    const timer = new TickerTimeout(() => {
      if (respin.current.busy) return;
      // for (let c = 0; c < HOLD_AND_WIN_COLUMNS; c++) {
      //   for (let r = 0; r < HOLD_AND_WIN_ROWS; r++) {
      //     const cell = cellRefs.current[c][r]?.current;
      //     const { id, skin } = resolveCell(kinds[c]?.[r], points?.[c]?.[r], jackpots);
      //     const value = values?.[c]?.[r] || null;
      //     if (id !== undefined && overlayRef?.current?.play(c, r, id, value, skin)) {
      //       cell?.hide();
      //     } else {
      //       overlayRef?.current?.clear(c, r);
      //       cell?.show(BLANK_SYMBOL_ID);
      //     }
      //   }
      // }
      // Shared with restore() (error recovery).
      paintGrid(cellRefs, overlayRef?.current, { kinds, values, points, jackpots });
    }, 50);
    return () => timer.clear();
  }, [devSpinLoop, kinds, values, points, jackpots, spritesheet, overlayRef]);

  if (!spritesheet?.textures || !cellWidth || !cellHeight) return null;

  const left = -((HOLD_AND_WIN_COLUMNS - 1) / 2) * cellWidth;
  const top = -((HOLD_AND_WIN_ROWS - 1) / 2) * cellHeight;

  return (
    <Container name="hold_and_win_board">
      {Array.from({ length: HOLD_AND_WIN_COLUMNS }, (_, c) =>
        Array.from({ length: HOLD_AND_WIN_ROWS }, (_, r) => (
          <Container
            key={`hnw_${c}_${r}`}
            x={left + c * cellWidth}
            y={top + r * cellHeight}
          >
            <HoldAndWinCell
              ref={cellRefs.current[c][r]}
              width={cellWidth}
              height={cellHeight}
              reelIndex={c}
              rowIndex={r}
              symbolId={BLANK_SYMBOL_ID}
              spritesheet={spritesheet}
              onLanded={handleLanded}
            />
          </Container>
        )),
      )}
    </Container>
  );
});

HoldAndWinBoard.displayName = "HoldAndWinBoard";

export default HoldAndWinBoard;
